// Pure Reading Basket operations. No storage or Firebase here, so the same
// logic powers guest (localStorage) and signed-in (Firestore) baskets.
import { LIMITS, type SavedStory } from "../../shared/model.ts";

export type Basket = SavedStory[];

/** Sorted copy with dense, 0-based order values. */
export function normalize(basket: Basket): Basket {
  const seen = new Set<string>();
  return [...basket]
    .sort((a, b) => a.order - b.order || a.savedAt - b.savedAt)
    .filter((item) => (seen.has(item.storyId) ? false : (seen.add(item.storyId), true)))
    .slice(0, LIMITS.maxSavedStories)
    .map((item, i) => ({ ...item, order: i }));
}

export function isSaved(basket: Basket, storyId: string): boolean {
  return basket.some((b) => b.storyId === storyId);
}

export function addStory(basket: Basket, storyId: string, now = Date.now()): Basket {
  if (isSaved(basket, storyId) || basket.length >= LIMITS.maxSavedStories) return basket;
  return normalize([
    ...basket,
    { storyId, savedAt: now, order: basket.length, isRead: false, readAt: null, readingProgress: null },
  ]);
}

export function removeStory(basket: Basket, storyId: string): Basket {
  return normalize(basket.filter((b) => b.storyId !== storyId));
}

export function move(basket: Basket, storyId: string, delta: -1 | 1): Basket {
  const list = normalize(basket);
  const i = list.findIndex((b) => b.storyId === storyId);
  const j = i + delta;
  if (i < 0 || j < 0 || j >= list.length) return list;
  [list[i], list[j]] = [list[j], list[i]];
  return list.map((item, k) => ({ ...item, order: k }));
}

export function setRead(basket: Basket, storyId: string, isRead: boolean, now = Date.now()): Basket {
  return basket.map((b) =>
    b.storyId === storyId
      ? { ...b, isRead, readAt: isRead ? now : null, readingProgress: isRead ? 1 : b.readingProgress === 1 ? 0 : b.readingProgress }
      : b,
  );
}

export function setProgress(basket: Basket, storyId: string, progress: number): Basket {
  const p = Math.min(1, Math.max(0, progress));
  return basket.map((b) =>
    b.storyId === storyId && !b.isRead && (b.readingProgress ?? 0) < p ? { ...b, readingProgress: p } : b,
  );
}

/** Where a reading session should resume: an in-progress item, else the first unread. */
export function resumeIndex(basket: Basket): number {
  const list = normalize(basket);
  const inProgress = list.findIndex((b) => !b.isRead && (b.readingProgress ?? 0) > 0);
  if (inProgress >= 0) return inProgress;
  const unread = list.findIndex((b) => !b.isRead);
  return unread >= 0 ? unread : 0;
}

export function totalMinutes(basket: Basket, minutesById: Map<string, number>, unreadOnly = false): number {
  return basket
    .filter((b) => !unreadOnly || !b.isRead)
    .reduce((sum, b) => sum + (minutesById.get(b.storyId) ?? 0), 0);
}

/**
 * Merge a guest basket into an account basket after sign-in.
 * - Account items keep their order; guest-only items are appended in guest order.
 * - No duplicates (by storyId).
 * - Read status is preserved from either side (read wins; earliest readAt kept).
 */
export function mergeBaskets(account: Basket, guest: Basket): Basket {
  const acc = normalize(account);
  const byId = new Map(acc.map((b) => [b.storyId, b]));
  const appended: Basket = [];
  for (const g of normalize(guest)) {
    const existing = byId.get(g.storyId);
    if (!existing) {
      appended.push(g);
      continue;
    }
    const isRead = existing.isRead || g.isRead;
    const readAts = [existing.readAt, g.readAt].filter((x): x is number => x !== null);
    byId.set(g.storyId, {
      ...existing,
      savedAt: Math.min(existing.savedAt, g.savedAt),
      isRead,
      readAt: isRead && readAts.length ? Math.min(...readAts) : null,
      readingProgress: maxNullable(existing.readingProgress, g.readingProgress),
    });
  }
  const merged = acc.map((b) => byId.get(b.storyId)!);
  return normalize([...merged, ...appended.map((g, i) => ({ ...g, order: merged.length + i }))]);
}

function maxNullable(a: number | null, b: number | null): number | null {
  if (a === null) return b;
  if (b === null) return a;
  return Math.max(a, b);
}

/** Items whose stored representation differs (or which are new) — used to minimize writes. */
export function changedItems(before: Basket, after: Basket): { upserts: SavedStory[]; deletes: string[] } {
  const prev = new Map(before.map((b) => [b.storyId, b]));
  const next = new Set(after.map((b) => b.storyId));
  const upserts = after.filter((b) => {
    const p = prev.get(b.storyId);
    return (
      !p ||
      p.order !== b.order ||
      p.isRead !== b.isRead ||
      p.readAt !== b.readAt ||
      p.readingProgress !== b.readingProgress ||
      p.savedAt !== b.savedAt
    );
  });
  const deletes = before.filter((b) => !next.has(b.storyId)).map((b) => b.storyId);
  return { upserts, deletes };
}
